import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { ZWEEFROUTE, ZWEEF_OMLOOP } from './indeling'
import type { Camerastand, Zweefpunt } from './indeling'
import { useKiosk } from '../state/store'
import { useAnimatie } from '../scene/store'
import { halklok, rondOm } from './omloop'
import { OPNAME } from './film/opname'

/**
 * Het punt waar de camera naar kijkt, gedeeld met de scherptediepte in
 * `Effecten.tsx`. Eén vector op moduleniveau en geen prop of store: hij
 * verandert elk frame, en een rerender per frame voor een focusafstand is
 * precies het soort werk dat de beurs-pc niet hoort te doen.
 */
export const focusPunt = new THREE.Vector3()

/** Per as drie sinussen: [hoeksnelheid, fase, gewicht]. */
type Trilling = [number, number, number][][]

const HANDHELD: Trilling = [
  [[0.61, 0, 0.6], [1.37, 1.1, 0.3], [2.93, 0.4, 0.1]],
  [[0.53, 2.0, 0.6], [1.19, 0.3, 0.3], [3.41, 2.2, 0.1]],
  [[0.47, 4.1, 0.6], [1.61, 2.7, 0.3], [2.71, 1.3, 0.1]],
]

/**
 * De trilling op tijd `t × tempo + verschuiving`, met elke hoeksnelheid
 * afgerond op een geheel aantal omwentelingen per omloop van de hal
 * (`omloop.ts`): dan staat de camera in de film van de zweefronde op het eind
 * precies waar hij begon.
 */
function maakTrilling(tempo: number, verschuiving: number): Trilling {
  return HANDHELD.map((as) => as.map(([w, fase, gewicht]) => {
    const rond = rondOm(w * tempo)
    return [rond, fase + rond * verschuiving, gewicht]
  }))
}
const POSITIE_TRILLING = maakTrilling(1, 0)
const KIJK_TRILLING = maakTrilling(1.13, 40)

/**
 * Handheld: drie sinussen per as met onderling ondeelbare frequenties, zodat
 * het patroon zich niet zichtbaar herhaalt (sinds 22 oktober pas na een
 * omloop van de hal, 245 s). Amplitudes in meters en radialen; bewust klein.
 * Wat het doet is niet "bewegen" maar het beeld de stilstand ontnemen — een
 * volkomen stilstaande camera leest als een screenshot (PLAN-STUDIO.md §2.5).
 */
function handheld(t: number, uit: THREE.Vector3, schaal: number, trilling: Trilling) {
  const as = (i: number) => trilling[i].reduce((s, [w, fase, gewicht]) => s + Math.sin(t * w + fase) * gewicht, 0)
  uit.set(as(0), as(1), as(2)).multiplyScalar(schaal)
}

/**
 * De camera en de film van de zweefronde (`Zweeffilm.tsx`). Eén object op
 * moduleniveau, zoals `focusPunt`: het verandert per frame, en de film kijkt
 * er per frame naar.
 *
 * - `opRoute`: de camera staat precies op de zweefroute. Pas dan is het beeld
 *   van de hal hetzelfde als dat van de film op dezelfde tijd.
 * - `vasthouden`: blijf op de route, ook als het scherm al een onderdeel is.
 *   Bij een tik op het hoofdscherm vloeit de film eerst over in de hal; pas
 *   daarna begint de vlucht, anders vloeien twee verschillende beelden in
 *   elkaar over.
 * - `springNaarRoute`: zet de camera dit frame meteen op de route. Na de film
 *   stond de hal stil; zijn camera staat nog waar de film begon.
 */
export const zweefStatus = { opRoute: false, vasthouden: false, springNaarRoute: false }

/**
 * Seconden waarin de camera na een vlucht terug naar de lus op de route
 * schuift. De demping laat hem er anders een kwart meter achteraan lopen.
 */
const INLOPEN = 1.6

/** Stappen in de tijdtabel van de route. */
const ROUTE_STAPPEN = 4000

/**
 * De zweefroute als twee gesloten krommen: één voor de camera, één voor het
 * punt waar hij naar kijkt. Beide lopen op dezelfde parameter, dus bij elk
 * routepunt kijkt de camera naar zijn eigen doel, en draait de blik mee met de
 * bocht in plaats van na te ijlen. `centripetal` voorkomt de lussen en
 * overshoots die een gewone Catmull-Rom bij ongelijk verdeelde punten maakt —
 * en de punten liggen hier bewust ongelijk, want ze volgen de hal en niet een
 * raster.
 *
 * De snelheid is constant over de boog, maal het `tempo` van de punten,
 * glad van punt naar punt: langs de Matics zweeft hij op halve snelheid. De
 * tabel zet tijd om in kromparameter, één keer vooraf.
 */
function maakRoute(punten: Zweefpunt[]) {
  const positie = new THREE.CatmullRomCurve3(punten.map((s) => new THREE.Vector3(...s.positie)), true, 'centripetal')
  const doel = new THREE.CatmullRomCurve3(punten.map((s) => new THREE.Vector3(...s.doel)), true, 'centripetal')
  const n = punten.length
  const tempo = (t: number) => {
    const x = t * n
    const i = Math.floor(x) % n
    const f = x - Math.floor(x)
    const a = punten[i].tempo ?? 1
    const b = punten[(i + 1) % n].tempo ?? 1
    return a + (b - a) * f * f * (3 - 2 * f)
  }
  const lengtes = positie.getLengths(ROUTE_STAPPEN)
  const tijd = new Float64Array(ROUTE_STAPPEN + 1)
  for (let i = 1; i <= ROUTE_STAPPEN; i++) {
    tijd[i] = tijd[i - 1] + (lengtes[i] - lengtes[i - 1]) / tempo((i - 0.5) / ROUTE_STAPPEN)
  }
  for (let i = 1; i <= ROUTE_STAPPEN; i++) tijd[i] /= tijd[ROUTE_STAPPEN]
  return { positie, doel, tijd }
}

/** Waar de camera en zijn doel zijn op fractie `u` (0–1) van de omloop. */
function routeStand(route: ReturnType<typeof maakRoute>, u: number, positie: THREE.Vector3, doel: THREE.Vector3) {
  const { tijd } = route
  let lo = 0
  let hi = ROUTE_STAPPEN
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1
    if (tijd[m] <= u) lo = m
    else hi = m
  }
  const t = (lo + (u - tijd[lo]) / Math.max(1e-12, tijd[hi] - tijd[lo])) / ROUTE_STAPPEN
  route.positie.getPoint(t, positie)
  route.doel.getPoint(t, doel)
}

/**
 * Camerarig. Eén ononderbroken beweging tussen standpunten in plaats van een
 * paginawissel — dat is wat het als doorlopen laat voelen (PLAN.md §5.1).
 *
 * Op het hoofdscherm (`zweef`) volgt de camera de zweefroute: hij staat nooit
 * stil en komt langs alle vijf de onderdelen. Zolang een vlucht naar een
 * standpunt loopt staat de invoer op slot (§2.4): zonder dat overschrijft een
 * tweede tik een lopende transitie halverwege.
 */
export function CameraRig({ stand, zweef }: { stand: Camerastand; zweef: boolean }) {
  const { camera } = useThree()
  const anim = useAnimatie()
  const doel = useRef(new THREE.Vector3(...stand.doel))
  const positieDoel = useRef(new THREE.Vector3(...stand.positie))
  const zetVergrendeld = useKiosk((s) => s.zetVergrendeld)
  const route = useMemo(() => maakRoute(ZWEEFROUTE), [])

  useEffect(() => {
    positieDoel.current.set(...stand.positie)
    doel.current.set(...stand.doel)
    zetVergrendeld(true)
    const id = setTimeout(() => zetVergrendeld(false), 1400)
    return () => clearTimeout(id)
  }, [stand, zetVergrendeld])

  const kijk = useRef(new THREE.Vector3(...stand.doel))
  // De rustpositie los van de camera: de handheld wordt er elk frame óp gezet,
  // niet ín gemengd. Mengen liet de demping hem optellen tot een factor twintig.
  const basis = useRef<THREE.Vector3 | null>(null)
  const gewenst = useRef(new THREE.Vector3())
  const trilling = useRef(new THREE.Vector3())
  const kijkTrilling = useRef(new THREE.Vector3())
  // Hoe ver de camera op de route is ingelopen, 0–1 (`INLOPEN`).
  const inLus = useRef(0)

  useFrame((_, delta) => {
    if (OPNAME.eigenCamera) return
    const dt = Math.min(delta, 0.1)
    // Framerate-onafhankelijke demping: bij 30fps even snel als bij 60fps.
    // Zet de film de klok, dan geen demping: elk beeld staat op zichzelf.
    const k = halklok.extern ? 1 : 1 - Math.pow(0.0016, dt)
    const beweeg = anim.aan && anim.cameraDrift
    // De klok van de hal (`omloop.ts`): de route, de trilling en de trucks
    // lopen op dezelfde tijd. Een bezoeker die terugkomt op het hoofdscherm
    // vliegt daarom naar waar de lus nu is, niet naar waar hij wegging.
    const tijd = halklok.t

    const lus = beweeg && (zweef || zweefStatus.vasthouden)
    if (lus) {
      // De zweefroute vervangt op het hoofdscherm de standen: de camera is
      // nooit ergens, hij is altijd onderweg. De demping hieronder zorgt dat
      // de overgang van een onderdeel terug naar de lus een vlucht is en geen
      // sprong; daarna schuift de camera in `INLOPEN` s precies op de route.
      routeStand(route, (tijd / ZWEEF_OMLOOP) % 1, gewenst.current, doel.current)
      // Zet de film de klok, dan staat de camera er meteen: in de opname
      // (Remotion), en in de hal zolang hij de film volgt.
      const meteen = halklok.extern || zweefStatus.springNaarRoute
      inLus.current = meteen ? 1 : Math.min(1, inLus.current + dt / INLOPEN)
    } else {
      inLus.current = 0
      gewenst.current.copy(positieDoel.current)
    }

    if (!basis.current) basis.current = camera.position.clone()
    basis.current.lerp(gewenst.current, k)
    kijk.current.lerp(doel.current, k)
    // Inlopen: zacht van de gedempte stand naar de route zelf. Het resultaat
    // is de nieuwe stand, dus wie de lus verlaat vliegt vanaf hier weg.
    const w = inLus.current * inLus.current * (3 - 2 * inLus.current)
    basis.current.lerp(gewenst.current, w)
    kijk.current.lerp(doel.current, w)
    zweefStatus.opRoute = lus && inLus.current >= 1
    zweefStatus.springNaarRoute = false
    camera.position.copy(basis.current)
    focusPunt.copy(kijk.current)

    if (beweeg) {
      handheld(tijd, trilling.current, 0.018, POSITIE_TRILLING)
      // De kijkrichting in hoekmaat (~0,2°), dus geschaald met de afstand tot
      // het doel: 5 cm op een truck van 8 m is iets anders dan op 50 m gangpad.
      handheld(tijd, kijkTrilling.current, 0.0035 * basis.current.distanceTo(kijk.current), KIJK_TRILLING)
      camera.position.add(trilling.current)
      camera.lookAt(kijkTrilling.current.add(kijk.current))
    } else {
      camera.lookAt(kijk.current)
    }
  })

  return null
}
